import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { fetchUser } from "../../api/users";

export function useUserQuery(options = {}) {
  const auth = useAuth();
  const queryClient = useQueryClient();

  const token = auth?.user?.access_token ?? null;
  const isAuthenticated = auth?.isAuthenticated;
  const logoutInProgress =
    typeof window !== "undefined" &&
    sessionStorage.getItem("logoutInProgress") === "1";

  /* ---------------------------------------------------------------------- */
  /* 🧠 React Query — run only when safe                                    */
  /* ---------------------------------------------------------------------- */
  const query = useQuery({
    queryKey: ["user"],
    queryFn: async () => fetchUser(token), // ✅ delegated to API helper
    enabled: Boolean(isAuthenticated && token && !logoutInProgress),
    retry: (failureCount, error) => {
      // ❌ Stop retries if auth invalid
      if (error.message === "unauthorized") return false;
      return failureCount < 2;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0, // 5 minutes
    gcTime: 0, // 30 minutes cache retention
    ...options,
  });

  /* ---------------------------------------------------------------------- */
  /* Manual refresh (e.g. user updated profile, needs fresh data)           */
  /* ---------------------------------------------------------------------- */
  const refreshUser = async () => {
    if (!token) return;
    await queryClient.invalidateQueries({ queryKey: ["user"] });
  };

  return { ...query, refreshUser };
}
